import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { TooltipContainer } from "@scripts/react/components/Tooltip";

const METRIC_STYLES = {
    purple: { bg: "bg-purple-200", fill: "bg-purple-500" },
    blue: { bg: "bg-blue-200", fill: "bg-blue-500" },
    amber: { bg: "bg-amber-200", fill: "bg-amber-500" },
    gray: { bg: "bg-gray-200", fill: "bg-gray-500" },
    green: { bg: "bg-green-200", fill: "bg-green-500" },
    yellow: { bg: "bg-yellow-200", fill: "bg-yellow-500" }
} as const;

export type MetricColor = keyof typeof METRIC_STYLES;

export function MetricBar({
    value,
    color = "gray",
    height = "h-12"
}: {
    value: number;
    color?: MetricColor;
    height?: string;
}) {
    const styles = METRIC_STYLES[color];
    return (
        <div
            className={twClassNames(
                "relative w-1.5 overflow-hidden rounded-full",
                styles.bg,
                height
            )}
        >
            <div
                className={twClassNames(
                    "absolute bottom-0 w-full rounded-full transition-all duration-500",
                    styles.fill
                )}
                style={{ height: `${Math.min(100, value)}%` }}
            />
        </div>
    );
}

export function MetricStat({
    label,
    description,
    value,
    color = "gray",
    isLoading = false
}: {
    label: string;
    description?: string;
    value: number | undefined;
    color?: MetricColor;
    isLoading?: boolean;
}) {
    if (isLoading) {
        const bgClass = METRIC_STYLES[color].bg;
        return (
            <div className="flex items-center gap-2.5">
                <div className={twClassNames("h-12 w-1.5 animate-pulse rounded-full", bgClass)} />
                <div className="flex flex-col gap-1">
                    <div className={twClassNames("h-4 w-12 animate-pulse rounded", bgClass)} />
                    <div className={twClassNames("h-5 w-10 animate-pulse rounded", bgClass)} />
                </div>
            </div>
        );
    }

    if (value === undefined) {
        return null;
    }

    return (
        <div className="flex items-center gap-2.5">
            <MetricBar value={value} color={color} height="h-12" />
            <div className="flex flex-col text-left">
                {description ? (
                    /* @ts-expect-error - TooltipContainer type issue */
                    <TooltipContainer
                        title={description}
                        type="titleOnly"
                        placement="top"
                        customButton={
                            <div className="text-xs text-gray-900/60 hover:cursor-help">
                                {label}
                            </div>
                        }
                    />
                ) : (
                    <span className="text-xs text-gray-500">{label}</span>
                )}
                <span className="font-mono text-sm font-semibold text-primary">
                    {value.toFixed(1)}%
                </span>
            </div>
        </div>
    );
}

export type MetricRaw = { id: string; title: string; description: string; value: number };

export type MetricInsight = {
    id: string;
    title: string;
    description: string;
    change: number;
    isImprovement: boolean;
};

type MetricCardProps =
    | { metric: MetricRaw; insight?: never }
    | { metric?: never; insight: MetricInsight };

export function MetricCard(props: MetricCardProps) {
    const isSingleMode = props.metric !== undefined;
    const data = isSingleMode ? props.metric : props.insight;

    return (
        <div
            className={twClassNames(
                "flex w-full items-center justify-between rounded-xl border px-4 py-2 text-sm",
                isSingleMode
                    ? "border-gray-200 bg-gray-50 text-gray-900"
                    : props.insight!.isImprovement
                      ? "border-green-500/20 bg-green-50 text-green-600"
                      : "border-red-100 bg-red-50 text-red-600"
            )}
        >
            <div className="flex items-center gap-1">
                {!isSingleMode && (
                    <i
                        className={twClassNames(
                            "far",
                            props.insight!.isImprovement ? "fa-arrow-up" : "fa-arrow-down"
                        )}
                    />
                )}
                <span className="font-mono font-semibold">
                    {isSingleMode
                        ? `${props.metric!.value.toFixed(1)}%`
                        : `${Math.abs(props.insight!.change).toFixed(1)}%`}
                </span>
            </div>

            {data!.description ? (
                /* @ts-expect-error - TooltipContainer type issue */
                <TooltipContainer
                    title={data!.description}
                    type="titleOnly"
                    placement="top"
                    customButton={
                        <div className="flex items-center gap-1 font-sans hover:cursor-help">
                            {data!.title}
                        </div>
                    }
                />
            ) : (
                <span className="font-normal">{data!.title}</span>
            )}
        </div>
    );
}
